/**
 * 기능 ID :
 * ASIS :
 */

import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppContext } from 'scbk-services';
import { usePopup } from 'scbk-shared-module';
import DeviceAuthComponent from 'scbk-shared-module/modules/DeviceAuth/DeviceAuthComponent';
import {
  AccordionUnder,
  BulletList,
  BulletListItem,
  IconButtonToggle,
  cn,
  useDevice,
} from 'scbk-ui-web';
import { ADDITIONAL_TYPE } from '../enums/additionalTypeEnum';
import { useAdditionalStore } from '../stores/useAdditionalStore';

interface Props {
  onComplete?: () => void;
}

const AdditionalIntroComponent: React.FC<Props> = ({ onComplete }) => {
  const { isDesktop } = useDevice();
  // 다국어 처리
  const { t } = useTranslation();

  const { getContext: appContext } = useAppContext();

  const {
    setStep,
    setCurrentAdditionalType,
    isSimple,
    isIdentify,
    isARS,
    isOverseasDeparture,
    activateInfo,
  } = useAdditionalStore();

  const additionalItems = [
    isSimple() && {
      id: ADDITIONAL_TYPE.SIMPLE,
      title: t('AdditionalComponent.001', '간편인증'),
      icon: 'ico-sample-authentication',
    },
    isARS() && {
      id: ADDITIONAL_TYPE.ARS,
      title: t('AdditionalComponent.003', '전화(ARS)인증'),
      icon: 'ico-tel',
    },
    isIdentify() && {
      id: ADDITIONAL_TYPE.IDENTIFY,
      title: t('AdditionalComponent.002', '휴대폰인증'),
      icon: 'ico-mobile-wallet',
    },
    isOverseasDeparture() && {
      id: ADDITIONAL_TYPE.OVERSEAS_DEPARTURE,
      title: t('AdditionalComponent.004', '해외체류인증'),
      icon: 'ico-globe',
    },
    appContext().channel === 'IB' &&
      (activateInfo?.transType?.includes('TRAN') || activateInfo?.transType?.includes('CERT')) && {
        id: ADDITIONAL_TYPE.DEVICE_AUTH,
        title: t('AdditionalComponent.126', 'PC 지정\n(Mac Address)'),
        icon: 'ico-desktop',
      },
  ].filter((item): item is { id: ADDITIONAL_TYPE; title: string; icon: string } => Boolean(item));

  const deviceAuthPopup = usePopup('deviceAuthPopup');

  const additionalConditions = [
    appContext().channel === 'IB' &&
      (activateInfo?.transType?.includes('TRAN') || activateInfo?.transType?.includes('CERT')),
    isSimple(),
    isIdentify(),
    isARS(),
    isOverseasDeparture(),
  ];

  const additionalCnt = additionalConditions.filter(Boolean).length;

  const onAdditionalChange = useCallback((id: string) => {
    switch (id) {
      case ADDITIONAL_TYPE.SIMPLE:
      case ADDITIONAL_TYPE.ARS:
      case ADDITIONAL_TYPE.IDENTIFY:
      case ADDITIONAL_TYPE.OVERSEAS_DEPARTURE:
        setStep(2);
        setCurrentAdditionalType(id);
        break;
      case ADDITIONAL_TYPE.DEVICE_AUTH:
        deviceAuthPopup.setTrigger();
        deviceAuthPopup.open();
        break;
    }
  }, []);

  return (
    <>
      <h2 className="page-heading-title mb-8">
        <Trans
          t={t}
          i18nKey="AdditionalComponent.005"
          components={[<span className="block md:inline md:mr-1"></span>]}
        >
          <span className="block md:inline md:mr-1">추가인증 방법을</span>
          선택해 주세요
        </Trans>
      </h2>

      <div
        className={cn(
          'grid grid-cols-2 gap-2',
          additionalCnt === 1 && !isDesktop && 'grid-cols-1',
          isDesktop && 'flex gap-3 justify-center',
        )}
      >
        <IconButtonToggle items={additionalItems} onChange={onAdditionalChange} />
      </div>
      {appContext().channel === 'IB' &&
        (activateInfo?.transType?.includes('TRAN') ||
          activateInfo?.transType?.includes('CERT')) && (
          <AccordionUnder
            openTitle={t('AdditionalComponent.124', '알아두세요')}
            className="hidden md:block"
          >
            <BulletList type="circle">
              <BulletListItem>
                {t(
                  'AdditionalComponent.125',
                  '단말기(PC)를 지정을 하시면 지정 단말기에서만 공동인증서 (재)발급, 타기관 공동인증서 사용등록 및 자금이체 서비스를 이용하실 수 있어요.',
                )}
              </BulletListItem>
            </BulletList>
          </AccordionUnder>
        )}
      {deviceAuthPopup.isOpen && (
        <DeviceAuthComponent
          isOpen={deviceAuthPopup.isOpen}
          setOpen={() => deviceAuthPopup.close()}
          onComplete={() => {
            onComplete?.();
            console.log('pcFix complete');
          }}
        />
      )}
    </>
  );
};

export default AdditionalIntroComponent;
